// @ts-nocheck
import React from 'react';
import { useSelector } from 'react-redux';
import {
  FiClock,
  FiAlertTriangle,
  FiActivity,
} from 'react-icons/fi';
import { GrServerCluster, GrGateway } from 'react-icons/gr';
import { GiVendingMachine } from 'react-icons/gi';
import { AiOutlineAudit } from 'react-icons/ai';
import {
  NoDataPlate,
  SpinnerBlock,
} from '@/components/organism/TableExpand/primitives';
import { SectionCard } from '@/components/organism/Cards/SectionCard';
import PillCell from '@/components/organism/StatusPills/PillCell';
import { CommonStepper } from '@/components/elements/Stepper';
import type { BaseStepperStep, StepStatus } from '@/components/elements/Stepper';
import { ORGANISM_PROPS } from '@/constant/global/global.constant';
import {
  APP_HEX,
  APP_SEMANTIC,
  APP_BORDER,
  APP_BG,
} from '@/constant/theme/colors.constant';
import { useTheme } from '@/context/theme.context';
import {
  smartCoolerTransactionsConfig,
  COOLER_TRANSACTION_STEPPER_CONTENT,
  ICoolerTransactionErrorProps,
} from '@/pages/CoolerTransaction/constant';
import { getDateTime, getDateTimeAgo } from '@/utils/dateTime';
import type { ICoolerTransaction } from '@/pages/CoolerTransaction/coolerTransaction.interface';
import { Text } from '@/components/atoms/AppText';
import { IconBadge } from '@/components/atoms/IconBadge';
import { COOLER_TRANSACTION_TEST_IDS as TID } from '@/constant/testIds/coolerTransaction.testIds';
import { tid } from '@/utils/testId';

const STEP_ICON: Record<string, React.ReactNode> = {
  VMS: <GrServerCluster />,
  GATEWAY: <GrGateway />,
  MMS: <GiVendingMachine />,
  CLIENTS: <AiOutlineAudit />,
};

/* ─── 2. WORKFLOW STEPPER ──────────────────────────────────────────────────── */

const HorizontalStepper = ({
  data,
  providerList,
}: {
  data: any;
  providerList: any[];
}) => {
  const { colors } = useTheme();
  const config = smartCoolerTransactionsConfig(providerList, data?.partnerUid);
  const { ENABLED_SMART_COOLER_STEPS } = config;

  if (!ENABLED_SMART_COOLER_STEPS?.length) {
    return (
      <SectionCard
        testId={tid(TID.EXPANDED_STEPPER)}
        title="Workflow Steps"
        icon={<FiActivity className="h-3.5 w-3.5" />}
      >
        <NoDataPlate
          message={ORGANISM_PROPS.COOLER_TRANSACTION_EXPANDED.NO_STEPPER_CONFIG}
        />
      </SectionCard>
    );
  }

  // Build raw statuses first, then promote the first not-reached → active
  const rawSteps: BaseStepperStep[] = ENABLED_SMART_COOLER_STEPS.map((step: any, i: number) => {
    const key = step.stepTitle?.toUpperCase() ?? '';
    const isFirst = i === 0;
    const act = data?.activity?.find(
      (a: any) => a?.server?.toUpperCase() === step.label?.toUpperCase(),
    );

    // Map success boolean directly to StepStatus — no hex intermediary needed
    const status: StepStatus = isFirst
      ? 'done'
      : act?.success === true
        ? 'done'
        : act?.success === false
          ? 'failed'
          : 'not-reached';

    const message = isFirst
      ? COOLER_TRANSACTION_STEPPER_CONTENT.FIRST_STEP_SUCCESS_MESSAGE
      : act?.message;

    return {
      title: key === 'CLIENTS' ? 'CLIENT' : key,
      date: isFirst ? data.createdOn : act?.activityDtm,
      status,
      iconElement: STEP_ICON[key] ?? <GrServerCluster />,
      tooltipContent: message || step.label,
    };
  });

  // Promote the first 'not-reached' step after all 'done' steps to 'active'
  // (only if there are no failed steps — if failed, next pending stays not-reached)
  const hasFailed = rawSteps.some((s) => s.status === 'failed');
  const steps: BaseStepperStep[] = hasFailed
    ? rawSteps
    : (() => {
      let promoted = false;
      return rawSteps.map((s) => {
        if (!promoted && s.status === 'not-reached') {
          promoted = true;
          return { ...s, status: 'active' as StepStatus };
        }
        return s;
      });
    })();

  return (
    <SectionCard
      title="Workflow Steps"
      icon={<FiActivity className="h-3.5 w-3.5" />}
    >
      <CommonStepper steps={steps} />
    </SectionCard>
  );
};

/* ─── 3. ACTIVITY FEED ─────────────────────────────────────────────────────── */
const ActivityFeed = ({ activity }: { activity: any[] }) => {
  if (!activity?.length) return null;
  const sorted = [...activity].sort(
    (a, b) =>
      new Date(a.activityDtm).getTime() - new Date(b.activityDtm).getTime(),
  );
  return (
    <SectionCard
      testId={tid(TID.EXPANDED_ACTIVITY)}
      title="Activity Log"
      icon={<FiClock className="h-3.5 w-3.5" />}
      badge={sorted.length}
    >
      <div
        className={`divide-y ${APP_BORDER.divideSoft} max-h-[340px] overflow-y-auto scrollbar-thin`}
      >
        {sorted.map((log, i) => {
          const ok = log.success === true;
          const err = log.success === false;
          const accent = ok
            ? APP_HEX.success
            : err
              ? APP_HEX.danger
              : APP_HEX.slate400;
          return (
            <div
              key={i}
              className="flex items-start gap-3 px-4 py-2.5 hover:bg-gray-50/60 dark:hover:bg-white/[0.02] transition-colors"
            >
              <div className="flex-shrink-0 mt-[9px]">
                <div
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: accent }}
                />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span
                    className="text-[10px] font-bold px-1.5 py-[1px] rounded-md"
                    style={{ background: `${accent}18`, color: accent }}
                  >
                    {log.server}
                  </span>
                  <Text
                    as="span"
                    size="sm"
                    weight="semibold"
                    uppercase
                    className={`${APP_SEMANTIC.neutral.bg} px-1.5 py-[1px] rounded-md`}
                    color="muted"
                  >
                    {log.action}
                  </Text>
                  {log.statusCode !== undefined && (
                    <span className="ml-auto">
                      <PillCell
                        value={log.statusCode}
                        preset="statusCode"
                        size="xs"
                      />
                    </span>
                  )}
                </div>
                {log.message && (
                  <Text
                    as="p"
                    size="md"
                    color="soft"
                    leading="snug"
                    clamp={1}
                    className="mt-0.5"
                  >
                    {log.message}
                  </Text>
                )}
              </div>
              <div className="flex-shrink-0 text-right min-w-fit">
                <Text
                  as="span"
                  size="xs"
                  color="muted"
                  className="font-mono whitespace-nowrap block"
                >
                  {getDateTime(log.activityDtm)}
                </Text>
                <Text
                  as="span"
                  size="xs"
                  color="faint"
                  className="whitespace-nowrap block"
                >
                  {getDateTimeAgo(log.activityDtm)}
                </Text>
              </div>
            </div>
          );
        })}
      </div>
    </SectionCard>
  );
};

/* ─── 4. ERRORS ───────────────────────────────────────────────────────────── */
const ERROR_HEADER_COLS = [
  'Status Code',
  'Object',
  'Error ID',
  'Message',
  'Date',
];

const ErrorsSection = ({
  errors,
}: {
  errors: ICoolerTransactionErrorProps[];
}) => {
  if (!errors?.length) return null;
  return (
    <SectionCard
      testId={tid(TID.EXPANDED_ERRORS)}
      title="Errors"
      icon={
        <IconBadge
          icon={<FiAlertTriangle className="h-3 w-3" />}
          size="sm"
          variant="custom"
          customStyle={{ background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.35)', color: 'white' }}
        />
      }
      badge={errors.length}
      className="border-red-200 dark:border-red-800/50"
      headerClassName="bg-[#E75065] dark:bg-[#c03d50] [&_p]:!text-white [&_span]:!text-white"
      iconVariant="custom"
    >
      <div className="overflow-x-auto -mx-4 sm:-mx-5 -mb-4 sm:-mb-5">
        {/* Table header */}
        <div className="grid grid-cols-[1fr_1.5fr_2fr_3fr_1.5fr] gap-2 px-4 py-2.5 bg-red-100 dark:bg-red-900/25 border-b border-red-200 dark:border-red-800/40">
          {ERROR_HEADER_COLS.map((h) => (
            <Text
              as="span"
              key={h}
              size="xs"
              weight="bold"
              color="danger"
              tracking="widest"
              uppercase
            >
              {h}
            </Text>
          ))}
        </div>
        {/* Rows */}
        <div className={errors.length > 3 ? 'overflow-y-auto max-h-[240px]' : ''}>
          {errors.map((e, i) => (
            <div
              key={i}
              className={[
                'grid grid-cols-[1fr_1.5fr_2fr_3fr_1.5fr] gap-2 px-4 py-3',
                'border-l-2 border-l-red-300 dark:border-l-red-600',
                'border-b border-red-100/80 dark:border-red-900/20 last:border-b-0',
                i % 2 === 0
                  ? 'bg-red-50/30 dark:bg-red-950/20'
                  : 'bg-white dark:bg-transparent',
                'hover:bg-red-50/60 dark:hover:bg-red-900/15 transition-colors',
              ].join(' ')}
            >
              <span className="inline-flex items-center self-start px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400 w-fit">
                {e.statusCode ?? '—'}
              </span>
              <Text as="span" size="md" color="soft" truncate title={e.objectName}>
                {e.objectName ?? '—'}
              </Text>
              <Text as="span" size="md" color="muted" className="font-mono break-all">
                {e.errorId ?? '—'}
              </Text>
              <Text as="span" size="md" weight="medium" className="break-words text-red-700 dark:text-red-300 self-start">
                {e.statusMessage ?? '—'}
              </Text>
              <Text as="span" size="md" color="muted" className="font-mono whitespace-nowrap">
                {e.createdOn ? getDateTime(e.createdOn) : '—'}
              </Text>
            </div>
          ))}
        </div>
      </div>
    </SectionCard>
  );
};

/* ─── MAIN EXPORT ─────────────────────────────────────────────────────────── */
export interface CoolerTransactionExpandedPanelProps {
  row: ICoolerTransaction;
  data?: any;
  loading?: boolean;
}

export const CoolerTransactionExpandedPanel = ({
  row,
  data,
  loading = false,
}: CoolerTransactionExpandedPanelProps) => {
  const providerList = useSelector(
    (state: any) => state?.provider?.providers?.data ?? [],
  );
  const isMatching = data?.transactionId === row?.transactionId;

  if (loading && !isMatching)
    return (
      <SpinnerBlock
        message={ORGANISM_PROPS.COOLER_TRANSACTION_EXPANDED.LOADING_MSG}
      />
    );
  if (!data && !loading)
    return (
      <NoDataPlate
        message={ORGANISM_PROPS.COOLER_TRANSACTION_EXPANDED.NO_DETAIL}
      />
    );

  const txData = isMatching ? data : null;
  const activity: any[] = txData?.activity ?? [];
  const errors: ICoolerTransactionErrorProps[] = txData?.errors ?? [];

  const hasErrors = errors.length > 0;
  const hasActivity = activity.length > 0;

  return (
    <div data-testid={tid(TID.EXPANDED_PANEL)} className="px-2 py-3 space-y-3">
      {/* Stepper — full width */}
      <HorizontalStepper data={txData ?? row} providerList={providerList} />
      {/* Activity + Errors — side by side */}
      {(hasActivity || hasErrors) && (
        <div
          className={[
            'grid gap-3',
            hasActivity && hasErrors
              ? 'grid-cols-1 lg:grid-cols-[1fr_2fr]'
              : 'grid-cols-1',
          ].join(' ')}
        >
          {hasActivity && <ActivityFeed activity={activity} />}
          {hasErrors && <ErrorsSection errors={errors} />}
        </div>
      )}
    </div>
  );
};
